/**
 * FeedTapePanel - Real-time Hydra message feed.
 *
 * Two side-by-side columns (Kalshi | Polymarket) showing the last 100
 * FeedLogEntry objects per venue, newest at top. Dense monospace layout.
 */

import { useMarketStore } from '@/stores/marketStore';
import type { FeedLogEntry } from '@/stores/marketStore';

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function fmtTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtPrice(cents: number | undefined): string {
  if (cents === undefined) return '\u2014';
  return `${cents}`;
}

// ---------------------------------------------------------------------------
// Single entry row
// ---------------------------------------------------------------------------

function EntryRow({ entry }: { entry: FeedLogEntry }) {
  const isMarket = entry.type === 'market';
  return (
    <div className="hover:bg-muted/30 flex items-center gap-1.5 px-2 py-px font-mono text-[10px] leading-4">
      <span className="text-muted-foreground shrink-0">{fmtTime(entry.ts)}</span>
      <span
        className={`shrink-0 rounded px-1 py-px text-[9px] font-bold ${
          isMarket ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
        }`}
      >
        {isMarket ? 'M' : 'B'}
      </span>
      <span className="text-muted-foreground shrink-0 uppercase">{entry.sport}</span>
      {entry.yesBid !== undefined || entry.yesAsk !== undefined ? (
        <span className="text-foreground truncate">
          <span className="text-green-400">{fmtPrice(entry.yesBid)}</span>
          <span className="text-muted-foreground mx-0.5">/</span>
          <span className="text-red-400">{fmtPrice(entry.yesAsk)}</span>
        </span>
      ) : entry.bookLevels !== undefined ? (
        <span className="text-muted-foreground truncate">{entry.bookLevels}lvl</span>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Column
// ---------------------------------------------------------------------------

function TapeColumn({ label, entries }: { label: string; entries: FeedLogEntry[] }) {
  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="border-border/50 flex h-6 shrink-0 items-center border-b px-2">
        <span className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
          {label}
        </span>
        <span className="text-muted-foreground/50 ml-auto text-[10px] tabular-nums">
          {entries.length}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {entries.length === 0 ? (
          <div className="text-muted-foreground/50 flex h-full items-center justify-center text-[10px]">
            No entries
          </div>
        ) : (
          entries.map((e, i) => <EntryRow key={`${e.ts}-${i}`} entry={e} />)
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeedTapePanel() {
  const getFeedLog = useMarketStore((s) => s.getFeedLog);
  const version = useMarketStore((s) => s.version);

  // Force re-derive on version tick
  void version;

  const kalshiEntries = getFeedLog('kalshi');
  const polyEntries = getFeedLog('polymarket');

  return (
    <div className="flex h-full divide-x divide-border">
      <TapeColumn label="Kalshi" entries={kalshiEntries} />
      <TapeColumn label="Polymarket" entries={polyEntries} />
    </div>
  );
}
